/**
 * AP-35 Nachtrag 4 (19.9.2026) — **Werkstoffe, die das Technik-Blatt kennt.**
 *
 * Aus einer Werkstoffangabe der Stückliste („S235JR+AR", „1.0038", „St 37-2",
 * „HEA 200 S355J2") wird die Sorte erkannt; aus der Sorte folgen die Felder,
 * die vom Werkstoff abhängen: Werkstoff-Hauptgruppe, Schweißeignung,
 * Bruchzähigkeit und Bauteil-Norm. Nur was die Liefernorm festlegt, steht hier —
 * erkennt die Liste eine Angabe nicht, wird nichts geraten.
 */

export interface Werkstoff {
  /** Kurzname nach Norm, z. B. `S235JR` */
  sorte: string
  /** Liefernorm, z. B. `EN 10025-2` */
  norm: string
  /** Wert für das Feld „Werkstoff-Hauptgruppe" */
  gruppe: string
  /** EN 1090-2 für Stahl und Edelstahl, EN 1090-3 für Aluminium */
  bauteilNorm: 'EN 1090-2' | 'EN 1090-3'
  /** Mindest-Kerbschlagarbeit nach Liefernorm */
  kerbschlag: string
  /** Werkstoffnummern und alte Bezeichnungen */
  alias: readonly string[]
}

const NPD = 'NPD (no performance determined)'
const RT = '27J bei RT'
const J0 = '27J bei 0 °C'
const J2 = '27J bei -20 °C'
const K2 = '40J bei -20 °C'
const L50 = '27J bei -50 °C'

const stahl = (sorte: string, norm: string, gruppe: string, kerbschlag: string, alias: string[] = []): Werkstoff => ({
  sorte,
  norm,
  gruppe,
  bauteilNorm: 'EN 1090-2',
  kerbschlag,
  alias,
})
const edelstahl = (nummer: string, kurz: string, gruppe = nummer): Werkstoff => ({
  sorte: nummer,
  norm: 'EN 10088-4',
  gruppe,
  bauteilNorm: 'EN 1090-2',
  kerbschlag: 'nach EN 10088-4',
  alias: [kurz],
})
const alu = (sorte: string, alias: string[]): Werkstoff => ({
  sorte,
  norm: 'EN 573-3',
  gruppe: 'Aluminium',
  bauteilNorm: 'EN 1090-3',
  kerbschlag: NPD,
  alias: [sorte.replace('EN AW-', 'AW-'), sorte.replace('EN AW-', 'AW'), ...alias],
})

export const WERKSTOFFE: readonly Werkstoff[] = [
  stahl('S235JR', 'EN 10025-2', 'S235', RT, ['1.0038', 'St37-2', 'RSt37-2', 'USt37-2', 'S235JRG2']),
  stahl('S235J0', 'EN 10025-2', 'S235', J0, ['1.0114', 'St37-3U']),
  stahl('S235J2', 'EN 10025-2', 'S235', J2, ['1.0117', 'St37-3N']),
  stahl('S275JR', 'EN 10025-2', 'S275', RT, ['1.0044', 'St44-2']),
  stahl('S275J0', 'EN 10025-2', 'S275', J0, ['1.0143', 'St44-3U']),
  stahl('S275J2', 'EN 10025-2', 'S275', J2, ['1.0145', 'St44-3N']),
  stahl('S355JR', 'EN 10025-2', 'S355', RT, ['1.0045']),
  stahl('S355J0', 'EN 10025-2', 'S355', J0, ['1.0553', 'St52-3U']),
  stahl('S355J2', 'EN 10025-2', 'S355', J2, ['1.0577', 'St52-3N', 'St52-3']),
  stahl('S355K2', 'EN 10025-2', 'S355', K2, ['1.0596']),
  // Hohlprofile — kalt gefertigt (EN 10219-1); warm gefertigte stehen in EN 10210-1 mit denselben Werten
  stahl('S235JRH', 'EN 10219-1', 'S235', RT, ['1.0039']),
  stahl('S275J0H', 'EN 10219-1', 'S275', J0, ['1.0149']),
  stahl('S275J2H', 'EN 10219-1', 'S275', J2, ['1.0138']),
  stahl('S355J0H', 'EN 10219-1', 'S355', J0, ['1.0547']),
  stahl('S355J2H', 'EN 10219-1', 'S355', J2, ['1.0576']),
  // Feinkornbaustähle
  stahl('S355N', 'EN 10025-3', 'S355', K2, ['1.0545']),
  stahl('S355NL', 'EN 10025-3', 'S355', L50, ['1.0546']),
  stahl('S355M', 'EN 10025-4', 'S355', K2, ['1.8823']),
  stahl('S355ML', 'EN 10025-4', 'S355', L50, ['1.8834']),
  stahl('S460N', 'EN 10025-3', 'S460', K2, ['1.8901']),
  stahl('S460M', 'EN 10025-4', 'S460', K2, ['1.8827']),
  // Nichtrostend
  edelstahl('1.4301', 'X5CrNi18-10'),
  edelstahl('1.4307', 'X2CrNi18-9'),
  edelstahl('1.4401', 'X5CrNiMo17-12-2'),
  edelstahl('1.4404', 'X2CrNiMo17-12-2'),
  edelstahl('1.4571', 'X6CrNiMoTi17-12-2'),
  // Aluminium
  alu('EN AW-6060', ['AlMgSi0,5', 'AlMgSi0.5']),
  alu('EN AW-6063', ['AlMg0,7Si', 'AlMg0.7Si']),
  alu('EN AW-6082', ['AlSi1MgMn', 'AlMgSi1']),
  alu('EN AW-6061', ['AlMg1SiCu']),
  alu('EN AW-5754', ['AlMg3']),
]

/** Die Werte für das Feld „Werkstoff-Hauptgruppe". */
export const WERKSTOFF_GRUPPEN = ['S235', 'S275', 'S355', 'S460', '1.4301', '1.4307', '1.4401', '1.4404', '1.4571', 'Aluminium', 'andere'] as const

/** So steht die Sorte im Feld „Schweißeignung" — wie auf der Leistungserklärung üblich. */
export const schweisseignungText = (w: Werkstoff) => `${w.sorte} nach ${w.norm}`

/** Großschrift, Komma als Punkt, Leerraum nur dort weg, wo er innerhalb einer Bezeichnung steht (`EN AW - 6060`, `St 37-2`). */
const norm = (s: string) =>
  s
    .toUpperCase()
    .replace(/,/g, '.')
    .replace(/\s*-\s*/g, '-')
    .replace(/\bEN\s+AW/g, 'ENAW')
    .replace(/\b(R?U?ST)\s+(\d)/g, '$1$2')
    .replace(/\s+/g, ' ')

/** Alle Schreibweisen, längste zuerst — sonst träfe `S235JR` vor `S235JRH`. */
const SCHREIBWEISEN: readonly [string, Werkstoff][] = WERKSTOFFE.flatMap((w) =>
  [w.sorte, ...w.alias].map((a) => [norm(a), w] as [string, Werkstoff]),
).sort((a, b) => b[0].length - a[0].length)

/**
 * Die Sorte in einer freien Angabe finden. Eine Schreibweise zählt nur als
 * eigenes Wort: danach kein Buchstabe und keine Ziffer (`S235JR+AR` ja,
 * `S235JRH` nicht als `S235JR`), davor weder Buchstabe noch Ziffer noch eine
 * Dezimalstelle (`11.4301` nicht, `W.-Nr. 1.4301` ja).
 */
export function erkenneWerkstoff(text: string | null | undefined): Werkstoff | null {
  if (!text) return null
  const t = norm(text)
  for (const [schreibweise, w] of SCHREIBWEISEN) {
    let ab = t.indexOf(schreibweise)
    while (ab >= 0) {
      const davor = t[ab - 1]
      const danach = t[ab + schreibweise.length]
      const davorFrei = !davor || (!/[A-Z0-9]/.test(davor) && !(davor === '.' && /[0-9]/.test(t[ab - 2] ?? '')))
      if (davorFrei && (!danach || !/[A-Z0-9]/.test(danach))) return w
      ab = t.indexOf(schreibweise, ab + 1)
    }
  }
  return null
}

/**
 * Was der Button „Hauptwerkstoff aus der Stückliste" ins Technik-Blatt schreibt —
 * je Leistungserklärungs-Feld (`dopField`), damit ein umbenanntes Feld getroffen wird.
 */
export function werkstoffVorschlag(w: Werkstoff): Record<'material' | 'weldability' | 'fractureToughness' | 'standard', string> {
  return {
    material: w.gruppe,
    weldability: schweisseignungText(w),
    fractureToughness: w.kerbschlag,
    standard: w.bauteilNorm,
  }
}

/** Antwort von `GET …/positions/:id/tech/main-material`. */
export interface HauptwerkstoffErgebnis {
  /** erkannte Sorte, `null` wenn keine Zeile einen bekannten Werkstoff trägt */
  werkstoff: Werkstoff | null
  /** die Angabe aus der Stückliste, wie sie dort steht */
  angabe: string | null
  /** Anteil am Gewicht (`basis = 'weight'`) oder an den Zeilen (`'count'`), 0…1 */
  anteil: number
  basis: 'weight' | 'count'
  /** Zeilen der Stückliste, Zeilen mit diesem Werkstoff, Zeilen mit unbekanntem Werkstoff */
  zeilen: number
  treffer: number
  unbekannt: string[]
  /** je `dopField` — leer, wenn nichts erkannt */
  vorschlag: Partial<Record<'material' | 'weldability' | 'fractureToughness' | 'standard', string>>
}
